import { useMemo, useState } from 'react';
import { loadStripe, type Stripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Loader2 } from 'lucide-react';
import { useTheme } from '@evi/skins';
import { Modal } from '@/components/ui/Modal';
import formStyles from '@/components/ui/Form.module.css';

/**
 * Card capture for the admin's pay-EVI surfaces (fleet Billing tab
 * today; the tenant billing card is the same shape).
 *
 * Mirrors apps/web/src/pages/billing/PaymentMethodsPage.tsx, the one
 * proven Elements wiring in the monorepo:
 *   1. the caller mints a SetupIntent server-side and hands us its
 *      client_secret;
 *   2. <Elements> is created around that secret, <PaymentElement>
 *      collects the card, and stripe.confirmSetup(redirect:
 *      'if_required') attaches it to the Stripe customer the intent
 *      was minted for;
 *   3. the caller's `onConfirmed(paymentMethodId)` persists it (the
 *      fleet tab posts it to the CONFIRM phase of
 *      /billing/update-payment-method, which sets the Stripe invoice
 *      default and the Fleet-doc mirror that session start and the
 *      "No card on file" state read).
 *
 * Step 3 is the caller's, not the webhook's: the protected
 * setup_intent.succeeded handler resolves USERS by customer id and
 * ignores fleet / tenant customers, so if it throws we keep the modal
 * open and offer a retry of just that step — the card is already saved
 * at Stripe by then, and re-running confirmSetup would fail on a
 * consumed intent.
 *
 * `redirect: 'if_required'` never redirects for a card-only SetupIntent
 * (3DS runs in Stripe's own iframe), so the inline success path is the
 * only one we handle; the return_url is a formality Stripe.js requires.
 *
 * No publishable key (CI builds never run SST; a laptop .env without
 * VITE_STRIPE_KEY) renders a plain "not configured" state instead of a
 * card form. The client_secret is never shown: it is a bearer token for
 * the intent, not something to hand around.
 */

const STRIPE_KEY = import.meta.env['VITE_STRIPE_KEY'] as string | undefined;
let stripePromise: Promise<Stripe | null> | null = null;
function getStripeP(): Promise<Stripe | null> {
  if (!STRIPE_KEY) return Promise.resolve(null);
  if (!stripePromise) stripePromise = loadStripe(STRIPE_KEY);
  return stripePromise;
}

/** True when this build can render a card form at all. */
export const STRIPE_ELEMENTS_CONFIGURED = Boolean(STRIPE_KEY);

export interface CardSetupModalProps {
  title?: string;
  /** SetupIntent client_secret from the server. */
  clientSecret: string;
  /** Stripe customer the intent belongs to — shown in the not-configured
   *  state so an operator can attach the card from the Dashboard. */
  stripeCustomerId?: string | null;
  /**
   * Persist the confirmed card. Runs after Stripe has attached it; a
   * throw keeps the modal open with the error and a retry of only this
   * step. Return normally to close.
   */
  onConfirmed: (paymentMethodId: string) => Promise<void>;
  onClose: () => void;
}

export function CardSetupModal({
  title = 'Update payment method', clientSecret, stripeCustomerId, onConfirmed, onClose,
}: CardSetupModalProps) {
  const { mode } = useTheme();
  // Stripe's two built-in themes track the admin's light/dark mode; the
  // brand green matches the accent the rest of the billing tab uses.
  const elementsOptions = useMemo(() => ({
    clientSecret,
    appearance: {
      theme: mode === 'light' ? ('stripe' as const) : ('night' as const),
      variables: { colorPrimary: '#2ECC71' },
    },
  }), [clientSecret, mode]);

  return (
    <Modal isOpen title={title} onClose={onClose} size="sm">
      {STRIPE_ELEMENTS_CONFIGURED ? (
        <Elements stripe={getStripeP()} options={elementsOptions}>
          <CardSetupForm onConfirmed={onConfirmed} onClose={onClose} />
        </Elements>
      ) : (
        <NotConfigured stripeCustomerId={stripeCustomerId ?? null} onClose={onClose} />
      )}
    </Modal>
  );
}

function CardSetupForm({
  onConfirmed, onClose,
}: Pick<CardSetupModalProps, 'onConfirmed' | 'onClose'>) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Set once Stripe has attached the card. From then on the only thing
  // left to do is the caller's persist step, so a failure there retries
  // that step alone instead of asking for the card again.
  const [savedPmId, setSavedPmId] = useState<string | null>(null);

  const persist = async (pmId: string) => {
    setSubmitting(true);
    setError(null);
    try {
      await onConfirmed(pmId);
    } catch (err) {
      setError(
        err instanceof Error && err.message
          ? err.message
          : 'The card was saved with Stripe but could not be set as the default. Retry.',
      );
      setSubmitting(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (savedPmId) {
      await persist(savedPmId);
      return;
    }
    if (!stripe || !elements) return;
    setSubmitting(true);
    setError(null);

    const result = await stripe.confirmSetup({
      elements,
      confirmParams: { return_url: window.location.href },
      redirect: 'if_required',
    });

    if (result.error) {
      setError(result.error.message ?? 'Stripe could not save this card.');
      setSubmitting(false);
      return;
    }
    // `setupIntent.payment_method` is a string id on the inline success
    // path. Defensive narrowing for the expanded-object case, as in the
    // PWA's AddCardForm.
    const pmRaw = result.setupIntent?.payment_method;
    const pmId =
      typeof pmRaw === 'string'
        ? pmRaw
        : pmRaw && typeof pmRaw === 'object' && 'id' in pmRaw
          ? String((pmRaw as { id: string }).id)
          : null;
    if (!pmId) {
      // Only reachable if Stripe took a redirect (not for card-only
      // intents). The card is attached at Stripe; the default must be
      // set by running the flow again or from the Stripe Dashboard.
      setError('Stripe saved the card but did not return it inline. Close this and run Update payment method again.');
      setSubmitting(false);
      return;
    }
    setSavedPmId(pmId);
    await persist(pmId);
  };

  return (
    <form onSubmit={handleSubmit} className={formStyles.form}>
      {error && <div className={formStyles.formError} role="alert">{error}</div>}
      {savedPmId ? (
        <p className={formStyles.hint}>
          The card is saved with Stripe. Setting it as the default did not complete; retry below.
        </p>
      ) : (
        <PaymentElement />
      )}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
        <button type="button" className={formStyles.btnSecondary} onClick={onClose} disabled={submitting}>
          Cancel
        </button>
        <button type="submit" className={formStyles.btnPrimary} disabled={(!stripe && !savedPmId) || submitting}>
          {submitting && <Loader2 size={12} className="spin" />}
          {submitting ? 'Saving…' : savedPmId ? 'Retry' : 'Save card'}
        </button>
      </div>
    </form>
  );
}

function NotConfigured({
  stripeCustomerId, onClose,
}: { stripeCustomerId: string | null; onClose: () => void }) {
  return (
    <div className={formStyles.form}>
      <div className={formStyles.formError} role="alert">
        Card entry is not available in this build: the Stripe publishable key
        (VITE_STRIPE_KEY) is not set.
      </div>
      <p className={formStyles.hint}>
        Attach a card from the Stripe Dashboard instead
        {stripeCustomerId ? <> on customer <code>{stripeCustomerId}</code></> : null}, then set it as the
        default payment method.
      </p>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
        <button type="button" className={formStyles.btnPrimary} onClick={onClose}>Close</button>
      </div>
    </div>
  );
}
